import React, { useEffect } from "react";
import {
  FiActivity,
  FiBarChart2,
  FiClock,
  FiUsers,
  FiVideo,
  FiClipboard,
  FiChevronsLeft,
  FiChevronsRight,
  FiCalendar,
  FiMessageSquare,
} from "react-icons/fi";

export type TabKey =
  | "live"
  | "summary"
  | "late"
  | "attendance"
  | "readiness"
  | "footage"
  | "roster"
  | "ticket";

type Props = {
  activeTab: TabKey;
  setActiveTab: (v: TabKey) => void;
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
};

const STORE_READINESS_ENABLED = process.env.REACT_APP_STORE_READINESS_ENABLED === "true";

const ITEMS: Array<{ key: TabKey; label: string; Icon: React.ComponentType<any> }> = [
  { key: "live", label: "Live", Icon: FiActivity },
  { key: "summary", label: "Daily summary", Icon: FiBarChart2 },
  { key: "late", label: "Late orders", Icon: FiClock },
  { key: "attendance", label: "Attendance", Icon: FiUsers },
  ...(STORE_READINESS_ENABLED
    ? [{ key: "readiness" as TabKey, label: "Store Readiness", Icon: FiClipboard }]
    : []),
  { key: "footage", label: "Footage", Icon: FiVideo },
  { key: "roster", label: "Store Roster", Icon: FiCalendar },
  { key: "ticket", label: "Log Ticket", Icon: FiMessageSquare },
];

const MANAGER_TABS: TabKey[] = ["attendance", "readiness", "roster", "ticket"];

function getUserRoles(): string[] {
  try {
    const raw = localStorage.getItem("gotrue.user");
    if (!raw) return [];
    const user = JSON.parse(raw);
    const token: string | undefined = user?.token?.access_token;
    if (!token) return [];
    const parts = token.split(".");
    if (parts.length !== 3) return [];
    const payload = JSON.parse(atob(parts[1]));
    const roles = payload?.app_metadata?.roles;
    return Array.isArray(roles) ? roles : [];
  } catch {
    return [];
  }
}

function isBranchManager(): boolean {
  return getUserRoles().some((r) => /^branch[A-Za-z]{3}$/.test(String(r)));
}

export function TowerSidebar({
  activeTab,
  setActiveTab,
  collapsed,
  setCollapsed,
}: Props) {
  const branchOnly = isBranchManager();

  const visibleItems = branchOnly
    ? ITEMS.filter((i) => MANAGER_TABS.includes(i.key))
    : ITEMS;

  useEffect(() => {
    if (branchOnly && !MANAGER_TABS.includes(activeTab)) {
      setActiveTab("readiness");
    }
  }, [branchOnly, activeTab, setActiveTab]);

  const width = collapsed
    ? "var(--ct-sidebar-w-collapsed)"
    : "var(--ct-sidebar-w)";

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        width,
        background: "var(--ct-surface)",
        borderRight: "1px solid var(--ct-line)",
        display: "flex",
        flexDirection: "column",
        transition: "width .16s ease",
        zIndex: 40,
      }}
    >
      <div
        style={{
          height: 61,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: collapsed ? "0 0 0 18px" : "0 16px",
          borderBottom: "1px solid var(--ct-line)",
          flex: "none",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: "var(--ct-accent)",
            color: "#fff",
            display: "grid",
            placeItems: "center",
            fontSize: 14,
            fontWeight: 700,
            flex: "none",
          }}
        >
          O
        </div>
        {!collapsed && (
          <div style={{ overflow: "hidden" }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 650,
                letterSpacing: "-0.01em",
                whiteSpace: "nowrap",
              }}
            >
              Orderlords
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--ct-ink-3)",
                whiteSpace: "nowrap",
              }}
            >
              Control Tower
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "10px 8px", flex: 1, overflowY: "auto" }}>
        {visibleItems.map(({ key, label, Icon }) => {
          const on = activeTab === key;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              title={collapsed ? label : undefined}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                width: "100%",
                padding: collapsed ? "10px 0" : "9px 11px",
                justifyContent: collapsed ? "center" : "flex-start",
                marginBottom: 2,
                borderRadius: 8,
                border: "none",
                cursor: "pointer",
                fontSize: 13.5,
                fontWeight: on ? 600 : 500,
                fontFamily: "inherit",
                textAlign: "left",
                background: on ? "var(--ct-accent-bg)" : "transparent",
                color: on ? "var(--ct-accent)" : "var(--ct-ink-2)",
                transition: "background .12s ease, color .12s ease",
              }}
              onMouseEnter={(e) => {
                if (!on) e.currentTarget.style.background = "var(--ct-surface-3)";
              }}
              onMouseLeave={(e) => {
                if (!on) e.currentTarget.style.background = "transparent";
              }}
            >
              <Icon size={17} style={{ flex: "none" }} />
              {!collapsed && <span style={{ whiteSpace: "nowrap" }}>{label}</span>}
            </button>
          );
        })}
      </div>

      <div
        style={{
          padding: "8px",
          borderTop: "1px solid var(--ct-line)",
          flex: "none",
        }}
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            width: "100%",
            padding: collapsed ? "10px 0" : "9px 11px",
            justifyContent: collapsed ? "center" : "flex-start",
            borderRadius: 8,
            border: "none",
            background: "transparent",
            color: "var(--ct-ink-3)",
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "inherit",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--ct-surface-3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
          }}
        >
          {collapsed ? <FiChevronsRight size={17} /> : <FiChevronsLeft size={17} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </nav>
  );
}
